import type { ComponentType, CSSProperties, ReactNode } from 'react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { Braces, Cpu, Database, GitBranch, Hash, Radio, Triangle } from 'lucide-react';
import type { HardwareNodeViewModel, NodeKind } from '../../types';

type ViewNode = Node<HardwareNodeViewModel>;
type ViewProps = NodeProps<ViewNode>;

function WidthBadge({ width }: { width: string }) {
  return <span className="hw-width-badge">{width}</span>;
}

function PinHandle({ id, type, position, top, className = '' }: { id: string; type: 'source' | 'target'; position: Position; top?: string; className?: string }) {
  const style: CSSProperties | undefined = top ? { top } : undefined;
  return <Handle id={id} className={`pin-terminal ${className}`} type={type} position={position} style={style} />;
}

function GenericHandles() {
  return <>
    <PinHandle id="a" className="pin-input" type="target" position={Position.Left} top="32%" />
    <PinHandle id="b" className="pin-input" type="target" position={Position.Left} top="68%" />
    <PinHandle id="y" className="pin-output" type="source" position={Position.Right} top="50%" />
  </>;
}

function Shell({ data, selected, kind = data.kind, children, handles = <GenericHandles /> }: ViewProps & { kind?: NodeKind; children: ReactNode; handles?: ReactNode }) {
  return <div className={`eda-node eda-${kind} ${selected ? 'is-selected' : ''}`} data-tooltip={`${data.label} · ${data.width}-bit ${kind}`}>
    {handles}
    {children}
  </div>;
}

export function RegisterNode(props: ViewProps) {
  const { data } = props;
  const resetLow = data.metadata.resetPolarity === 'active_low';
  return <Shell {...props} handles={<>
    <PinHandle id="d" className="pin-input" type="target" position={Position.Left} top="32%" />
    {data.metadata.enable && <PinHandle id="en" className="pin-control" type="target" position={Position.Left} top="54%" />}
    {data.metadata.reset && <PinHandle id="rst" className="pin-reset" type="target" position={Position.Left} top="72%" />}
    <PinHandle id="clk" className="pin-clock" type="target" position={Position.Left} top="90%" />
    <PinHandle id="q" className="pin-output" type="source" position={Position.Right} top="32%" />
  </>}>
    <div className="device-header"><span>REG</span><b>{data.label}</b><WidthBadge width={data.widthLabel} /></div>
    <div className="register-body">
      <span className="pin-label pin-d">D</span><span className="pin-label pin-q">Q</span>
      <span className="control-pin clock-pin"><Triangle size={9} fill="currentColor" /> CLK</span>
      {data.metadata.enable && <span className="control-pin enable-pin">E&nbsp; EN</span>}
      {data.metadata.reset && <span className="control-pin reset-pin">{resetLow ? '○' : ''}R&nbsp; {String(data.metadata.reset).toUpperCase()}</span>}
    </div>
  </Shell>;
}

export function MuxNode(props: ViewProps) {
  const { data } = props;
  const inputCount = Math.max(2, Number(data.metadata.inputCount ?? 2));
  const structure = String(data.metadata.structure ?? 'if_else').replaceAll('_', ' ');
  return <div className={`eda-node mux-node ${props.selected ? 'is-selected' : ''}`} data-tooltip={`${data.label} · multiplexer`}>
    {Array.from({ length: inputCount }, (_, index) => {
      const top = `${22 + (index * 56) / Math.max(1, inputCount - 1)}%`;
      return <span className="mux-input-pin" style={{ top }} key={index}><PinHandle id={`in-${index}`} className="pin-input" type="target" position={Position.Left} top="50%" /><em>I{index}</em></span>;
    })}
    <PinHandle id="y" className="pin-output" type="source" position={Position.Right} top="50%" />
    <PinHandle id="sel" className="pin-control" type="target" position={Position.Bottom} />
    <div className="mux-shape"><span className="mux-title">{inputCount}:1 MUX</span><b>{data.label}</b><small>{structure}</small><WidthBadge width={data.widthLabel} /><span className="mux-y">Y</span></div>
    <span className="mux-select">SEL · {String(data.metadata.select ?? 'select')}</span>
  </div>;
}

export function ArithmeticNode(props: ViewProps) {
  const { data } = props;
  return <div className={`eda-node arithmetic-node ${props.selected ? 'is-selected' : ''}`} data-tooltip={`${data.label} · ${data.width}-bit operator`}>
    <GenericHandles />
    <div className="alu-shape"><span className="alu-kind">{data.label}</span><strong>{data.operation}</strong><WidthBadge width={data.widthLabel} /><span className="alu-a">A</span><span className="alu-b">B</span><span className="alu-y">Y</span></div>
  </div>;
}

export function PortNode(props: ViewProps) {
  const input = props.data.kind === 'input';
  return <Shell {...props} handles={input
    ? <PinHandle id="out" className="pin-output" type="source" position={Position.Right} top="50%" />
    : <PinHandle id="in" className="pin-input" type="target" position={Position.Left} top="50%" />}>
    <div className="port-body"><span className="port-direction">{input ? 'IN ▸' : '▸ OUT'}</span><b>{props.data.label}</b><WidthBadge width={props.data.widthLabel} /></div>
  </Shell>;
}

export function ConstantNode(props: ViewProps) {
  return <Shell {...props} handles={<PinHandle id="out" className="pin-output" type="source" position={Position.Right} top="50%" />}><div className="constant-body"><Hash size={12} /><code>{props.data.label}</code><WidthBadge width={props.data.widthLabel} /></div></Shell>;
}

export function LogicGateNode(props: ViewProps) {
  const operation = String(props.data.metadata.operation ?? props.data.label).toLowerCase();
  const unary = operation.includes('not');
  const symbol = operation.includes('xor') ? '=1' : operation.includes('or') ? '≥1' : operation.includes('not') ? '1' : '&';
  const inputs = props.data.pins.filter((pin) => pin.direction === 'input');
  const height = Math.max(92, 48 + inputs.length * 18);
  return <div className={`eda-node logic-gate gate-${operation} ${props.selected ? 'is-selected' : ''}`} style={{ height }} data-tooltip={`${props.data.label} · ${inputs.length}-input inferred logic gate`}>
    {inputs.map((pin, index) => {
      const top = unary ? '50%' : `${24 + (index * 52) / Math.max(1, inputs.length - 1)}%`;
      return <PinHandle key={pin.id} id={pin.id} className="pin-input" type="target" position={Position.Left} top={top} />;
    })}
    <PinHandle id="y" className="pin-output" type="source" position={Position.Right} top="50%" />
    <div className="gate-shape"><span>{props.data.label}</span><strong>{symbol}</strong><WidthBadge width={props.data.widthLabel} />{inputs.map((pin, index) => <i className="gate-input-label" style={{ top: unary ? '50%' : `${24 + (index * 52) / Math.max(1, inputs.length - 1)}%` }} key={pin.id}>{pin.label}</i>)}<i className="gate-y">Y</i>{unary && <em className="inversion-bubble" />}</div>
  </div>;
}

export function ComparisonNode(props: ViewProps) {
  return <div className={`eda-node comparison-node ${props.selected ? 'is-selected' : ''}`} data-tooltip={`${props.data.label} · comparator`}>
    <GenericHandles />
    <div className="comparison-shape"><small>COMPARE</small><strong>{props.data.operation}</strong><span>A</span><span>B</span><i>Y</i></div>
  </div>;
}

const genericIcons: Partial<Record<NodeKind, ComponentType<{ size?: number }>>> = {
  logic: Braces, comparison: Radio, fsm: GitBranch, memory: Database, module: Cpu, unknown: Cpu,
};

export function GenericHardwareNode(props: ViewProps) {
  const Icon = genericIcons[props.data.kind] ?? Braces;
  return <Shell {...props}>
    <div className="generic-hardware-body"><span className="generic-icon"><Icon size={15} /></span><span><small>{props.data.kind}</small><b>{props.data.label}</b></span><WidthBadge width={props.data.widthLabel} /></div>
  </Shell>;
}

export const hardwareNodeTypes = {
  'hw-register': RegisterNode,
  'hw-mux': MuxNode,
  'hw-arithmetic': ArithmeticNode,
  'hw-input': PortNode,
  'hw-output': PortNode,
  'hw-constant': ConstantNode,
  'hw-logic': LogicGateNode,
  'hw-comparison': ComparisonNode,
  'hw-fsm': GenericHardwareNode,
  'hw-memory': GenericHardwareNode,
  'hw-module': GenericHardwareNode,
  'hw-unknown': GenericHardwareNode,
};
